/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/require-default-props */
import { Box } from '@material-ui/core';
import { ChatHistoryItem as ChatHistoryItemType } from '@src/reducers/chatHistory';
import {
  ChatEvent,
  ChatEventPayload,
  TimelineEvent,
  TimelineEventIntention,
} from '@src/types';
import { ChatContact } from '@src/types/chats';
import React, { useEffect, useMemo } from 'react';
import ChatMessage from '../ChatMessage';
import ChatMessageCallPermissionButton from '../ChatMessage/ChatMessageCallPermissionButton';
import ChatMessageActions from '../ChatMessageActions';
import InstagramStoryMessage from '../InstagramStoryMessage';
import ChatHistoryItemSeparator from './ChatHistoryItemSeparator';

type ChatHistoryItemProps = {
  prev?: ChatHistoryItemType;
  current: ChatHistoryItemType;
  next?: ChatHistoryItemType;
  isScrolling?: boolean;
  completed: boolean;
  firstItemIndex: number;
  contact: ChatContact;
};

const ChatHistoryItem: React.FC<ChatHistoryItemProps> = ({
  completed,
  firstItemIndex,
  isScrolling = false,
  prev,
  current,
  next,
  contact,
}) => {
  // Note:
  // the payload from WS and /services/console/chat-time-line/query are differents for representation of a message, then
  // the following conditions are required in order to render them correctly.
  const message: TimelineEvent = useMemo(() => {
    const currMessage = current.event;
    return {
      ...currMessage,
      isCaptionDefined:
        currMessage?.isCaptionDefined === undefined
          ? Boolean(currMessage?.mediaUrl && currMessage?.text) // for WS 'isCaptionDefined' isn't defined, so this condition identify message with caption
          : currMessage?.isCaptionDefined,
    };
  }, [current]);

  const disabledActions = useMemo(
    () =>
      message.disabledActions ||
      message?.isAutomaticMessage ||
      message?.isBroadcastWapMsg ||
      message?.messageFromBot,
    [message]
  );

  useEffect(() => {
    if (
      message?.agentChatButtons &&
      next?.event?.quotedMessage?.agentChatButtons &&
      message?.text?.includes(next?.event?.quotedMessage?.text)
    )
      message?.agentChatButtons.buttons.forEach(button => {
        if (button.tittle === next?.event?.text) button.active = true;
      });
  }, [message, next]);

  return (
    <ChatHistoryItemSeparator
      prev={prev}
      current={current}
      next={next}
      completed={completed}
      firstItemIndex={firstItemIndex}
      isScrolling={isScrolling}
    >
      <>
        {message?.context === 'STORY' ? (
          <InstagramStoryMessage
            message={
              {
                type: `${message.direction}_MESSAGE`,
                incomingMessage: message.direction === 'INCOMING',
                outgoingMessage: message.direction === 'OUTGOING',
                payload: message as ChatEventPayload,
                timestamp: message.sendingTime || 0,
                isBroadcastWapMsg: message.isBroadcastWapMsg,
                isAutomaticMessage: message.isAutomaticMessage,
              } as ChatEvent
            }
          />
        ) : (
          <ChatMessageActions
            chatId={current.chat.id}
            message={message}
            disableActions={disabledActions ?? false}
          >
            <Box display="flex" flexDirection="column">
              <ChatMessage contact={contact} message={message} />
              {message.intention ===
                TimelineEventIntention.REQUEST_CALL_PERMISSION && (
                <ChatMessageCallPermissionButton />
              )}
            </Box>
          </ChatMessageActions>
        )}
      </>
    </ChatHistoryItemSeparator>
  );
};

ChatHistoryItem.displayName = 'ChatHistoryItem';

export default ChatHistoryItem;
